"use client";

import { useEffect, useRef } from "react";
import { useNotification, useNotificationItem } from "@/modules/hooks/notification/useNotification";
import { useUnreadConversations } from "@/modules/hooks/conversation/useUnreadConversations";
import { NotificationMessageDto } from "@/modules/types/notification/notification";
import { Notification01Icon, Message01Icon } from "hugeicons-react";
import { routes } from "@/lib/utils/routes";
import Link from "next/link";
import { useClientTranslations } from "@/lib/translate";
import { useRouter } from "next/navigation";

interface NotificationDropdownProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

const NotificationDropdown = ({
  isOpen,
  onToggle,
  onClose,
}: NotificationDropdownProps) => {
  const { t } = useClientTranslations("notification");
  const router = useRouter();
  const {
    unReadNotifications,
    totalUnReadNotification,
    isLoading,
    markNotificationAsSeen,
    markNotificationAsRead,
  } = useNotification();

  const { totalUnreadConversations } = useUnreadConversations();
  const { generateContent } = useNotificationItem();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleNotificationClick = async (
    notification: NotificationMessageDto
  ) => {
    // Mark as seen and read
    await markNotificationAsSeen(notification.id);
    await markNotificationAsRead(notification.id);

    // Navigate to action URL if available
    if (notification.actionUrl) {
      router.push(notification.actionUrl);
    }

    onClose();
  };

  // const formatNotificationTime = (dateString?: string) => {
  //   if (!dateString) return "";

  //   const date = new Date(dateString);
  //   const now = new Date();
  //   const diffInMinutes = Math.floor(
  //     (now.getTime() - date.getTime()) / (1000 * 60)
  //   );

  //   if (diffInMinutes < 1) return "Just now";
  //   if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  //   if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
  //   return `${Math.floor(diffInMinutes / 1440)}d ago`;
  // };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="relative p-2 rounded-full hover:bg-secondaryLighter dark:hover:bg-gray-700 transition-colors duration-200"
        aria-label="Notifications"
        onClick={onToggle}
      >
        <Notification01Icon className="w-6 h-6" />
        {(totalUnReadNotification > 0 || totalUnreadConversations > 0) && (
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
        )}
      </button>

      {isOpen && (
        <div className="absolute -right-10 mt-2 w-80 bg-white dark:bg-gray-900 rounded-md shadow-lg py-2 z-50 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 max-w-[calc(100vw-1rem)] sm:max-w-none">
          <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
              {t("newNotification")}{" "}
              {(totalUnReadNotification > 0 || totalUnreadConversations > 0) &&
                `(${Number(totalUnReadNotification) +
                Number(totalUnreadConversations)
                })`}
            </h3>
          </div>

          <div className="max-h-60 overflow-y-auto">
            {/* Unread Conversations Notification */}
            {totalUnreadConversations > 0 && (
              <div className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 cursor-pointer border-b border-gray-50 dark:border-gray-700">
                <Link
                  href={routes.candidate.message}
                  className="block"
                  onClick={onClose}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                      <Message01Icon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">
                        {t("unreadConversations", {
                          count: totalUnreadConversations,
                          plural: totalUnreadConversations > 1 ? "s" : "",
                        })}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t("viewMessages")}
                      </p>
                    </div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1"></div>
                  </div>
                </Link>
              </div>
            )}

            {isLoading ? (
              <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2">{t("loading")}</p>
              </div>
            ) : totalUnReadNotification === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                <Notification01Icon className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                <p>{t("noNotifications")}</p>
              </div>
            ) : (
              unReadNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 cursor-pointer border-b border-gray-50 dark:border-gray-700 last:border-b-0 ${!notification.isSeen ? "bg-blue-50 dark:bg-blue-900/20" : ""
                    }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-3">
                    {notification.image && (
                      <img
                        src={notification.image}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-xs sm:text-sm ${!notification.isSeen ? "font-semibold" : ""
                          } text-gray-900 dark:text-white line-clamp-2`}
                      >
                        {notification.title || t("newNotification")}
                      </p>
                      {notification.content && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                          {generateContent(notification)}
                        </p>
                      )}
                      {/* <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {formatNotificationTime(notification.createdAt)}
                      </p> */}
                    </div>
                    {!notification.isSeen && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1"></div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {totalUnReadNotification > 0 && (
            <div className="border-t border-gray-100 dark:border-gray-700 mt-2">
              <Link
                href={routes.candidate.notification}
                className="block px-4 py-2 text-xs sm:text-sm text-primary hover:bg-gray-50 dark:hover:bg-gray-800 text-center transition-colors duration-200"
                onClick={onClose}
              >
                {t("viewAll")}
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
